import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useAppContext } from '@/components/AppContext'
import withDefaultLayout from '@/hoc/WithDefaultLayout'
import AdminBreadcrumb from '@/ui/organisms/AdminBreadcrumb'
import { message } from 'antd'
import { CreateStore } from '@a4b/api/src/modules/Monetisation/Puja/types'
import type { TablePaginationConfig } from 'antd/es/table'
import Error from '@/components/Error'
import { openErrorNotification, openNotification } from '@/utils'
import { useSearchParams } from 'react-router-dom'
import StoreListTable from './components/StoreListTable'
import StoreActivationModal from './components/StoreActivationModal'
import { A4B_APPS, SHOW_DEPRECATION_BANNER } from '@/utils/constants'
import MonetisationPujaApi from '@a4b/api/src/modules/Monetisation/Puja'
import { showErrorToast } from '@/utils/helper'
import DeprecationBanner from '@/components/DeprecationBanner'

const StoreList = () => {
  const { networkInstance, languageState, countryState, userProfile, app } = useAppContext()
  const { monetisationPujaApi, monetisationAstroPujaApi } = networkInstance.clientWithHeaders
  const api: MonetisationPujaApi = app === A4B_APPS.SRIMANDIR ? monetisationPujaApi : monetisationAstroPujaApi
  const isSrimandir = app === A4B_APPS.SRIMANDIR

  const [showStoreEnableModal, setShowStoreEnableModal] = useState(false)
  const [showStoreDisableModal, setShowStoreDisableModal] = useState(false)
  const [pagination, setPagination] = useState<TablePaginationConfig>({ current: 1, pageSize: 15 })
  const [storeList, setStoreList] = useState<CreateStore[]>([])
  const [currentItem, setCurrentItem] = useState<CreateStore>()
  const [errorPage, setErrorPage] = useState(false)
  const [activeCloneId, setActiveCloneId] = useState<string>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [isLastPage, setIsLastPage] = useState(false)

  const skipNextPaginationRef = useRef(false)
  const getPageBreadcrumbs = useMemo(() => [
    {
      path: isSrimandir ? '/monetisation/puja/puja-updates' : '/astro/puja/puja-updates',
      breadcrumbName: 'Puja Updates',
    },
    // {
    //   path: isSrimandir ? '/monetisation/puja' : '/astro/puja',
    //   breadcrumbName: 'Hub List',
    // },
    {
      path: isSrimandir ? '/monetisation/puja/temples' : '/astro/puja/temples',
      breadcrumbName: 'Manage Stores',
    },
  ], [isSrimandir])

  useEffect(() => {
    const hasFilterParams = searchParams.get('sort_by') ||
      searchParams.get('is_archive') ||
      searchParams.get('is_active') ||
      searchParams.get('service_type')

    if (!hasFilterParams) {
      setPagination((prev) => ({ ...prev, current: 1 }))
    }
  }, [])

  useEffect(() => {
    skipNextPaginationRef.current = true
    setPagination((prev) => ({ ...prev, current: 1 }))
  }, [
    searchParams.get('sort_by'),
    searchParams.get('is_archive'),
    searchParams.get('is_active'),
    searchParams.get('service_type'),
    searchParams.get('refresh')
  ])

  const getStoreList = useCallback(async () => {
    try {
      setIsLoading(true)
      const limit = pagination.pageSize || 15
      const offset = (pagination.current! - 1) * limit
      const title = searchParams.get('title') || undefined

      const storeList = await api.storesApi.getStoreList(
        { limit, offset },
        countryState?.countryCode,
        undefined,
        undefined,
        searchParams.get('sort_by') as ('created_at' | 'position') || 'created_at',
        searchParams.get('is_archive'),
        searchParams.get('is_active') || 'true',
        searchParams.get('service_type') || 'pooja',
        searchParams.get('id') || undefined,
        title
      )

      if (!storeList?.data?.data?.store_list?.length) {
        setStoreList([])
        setIsLastPage(true)
        setIsLoading(false)
        message.info({ content: 'No store found', duration: 3 })
        return
      }

      setStoreList(storeList.data.data.store_list)
      setIsLastPage(storeList.data.data.store_list.length < 15)
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      setErrorPage(true)
      message.error({ content: 'Error while fetching store list', duration: 3 })
      console.log(error)
    }
  }, [api.storesApi, pagination, countryState?.countryCode, searchParams])

  useEffect(() => {
    if (skipNextPaginationRef.current) {
      skipNextPaginationRef.current = false
      return
    }
    getStoreList()
  }, [getStoreList, pagination, languageState.monetisationLanguage, countryState?.countryCode])

  const updateFilters = (
    updateType: 'sortBy' | 'isArchive' | 'isActive' | 'serviceType' | 'title' | 'id',
    value?: 'true' | 'false' | 'created_at' | 'position' | 'pooja' | 'offerings' | 'promoter' | 'mandir_connect' | 'all' | string
  ) => {
    const paramMapping: Record<string, string> = {
      sortBy: 'sort_by',
      isArchive: 'is_archive',
      isActive: 'is_active',
      serviceType: 'service_type',
      id: 'id',
      title: 'title',
    }

    const paramName = paramMapping[updateType]

    if (paramName && value !== undefined && value !== '') {
      searchParams.set(paramName, value)
    } else if (paramName) {
      searchParams.delete(paramName)
    }
    setSearchParams(searchParams)
  }

  const cloneStore = useCallback(async (storeId, hubId: string) => {
    setActiveCloneId(storeId)
    try {
      const res = await api.storesApi.cloneStore(storeId, userProfile?.email || 'not found', hubId)
      getStoreList()
      openNotification("success", { message: res.data.data })
    } catch (error) {
      console.log(error)
      openErrorNotification(error)
    }
    setActiveCloneId(undefined)
  }, [getStoreList, api.storesApi, userProfile?.email])

  const onStatusChange = useCallback((item: CreateStore) => {
    setCurrentItem(item)
    item.active ? setShowStoreDisableModal(true) : setShowStoreEnableModal(true)
  }, [])

  const onModalCancel = () => {
    setShowStoreEnableModal(false)
    setShowStoreDisableModal(false)
  }

  const onModalSuccess = () => {
    setShowStoreEnableModal(false)
    setShowStoreDisableModal(false)
    getStoreList()
  }

  const archiveStore = useCallback(async (storeId: string) => {
    try {
      await api.storesApi.archiveStore(storeId, {
        email: userProfile.email,
        archive: true
      })
      getStoreList()
      openNotification("success", { message: "Archive status updated." })
    } catch {
      openNotification("error", { message: "Unable to archive store something went wrong" })
    }
  }, [api.storesApi, getStoreList, userProfile.email])

  const unarchiveStore = useCallback(async (storeId: string) => {
    try {
      await api.storesApi.archiveStore(storeId, {
        email: userProfile.email,
        archive: false
      })
      getStoreList()
      openNotification("success", { message: "Archive status updated." })
    } catch {
      openNotification("error", { message: "Unable to archive store something went wrong" })
    }
  }, [api.storesApi, getStoreList, userProfile.email])

  return (
    <div style={{ padding: '0 40px' }}>
      {errorPage ? (
        <Error text='Error while fetching Stores!' />
      ) : (
        <>
          <AdminBreadcrumb routes={getPageBreadcrumbs} />
          {SHOW_DEPRECATION_BANNER && <DeprecationBanner />}
          <StoreListTable
            onStatusChange={onStatusChange}
            templeList={storeList}
            archiveStore={archiveStore}
            unarchiveStore={unarchiveStore}
            cloneStore={cloneStore}
            activeCloneId={activeCloneId}
            sortBy={searchParams.get('sort_by') as ('created_at' | 'position') || 'created_at'}
            isArchive={searchParams.get('is_archive') as ('true' | 'false')}
            isActive={searchParams.get('is_active') as ('true' | 'false') || 'true'}
            updateFilters={updateFilters}
            loading={isLoading}
            pagination={pagination}
            onPageChange={(current: number) => setPagination((prev) => ({ ...prev, current }))}
            isLastPage={isLastPage}
          />
          {showStoreEnableModal && (
            <StoreActivationModal
              visible={showStoreEnableModal}
              isActivate={true}
              currentItem={currentItem}
              onCancel={onModalCancel}
              onSuccess={onModalSuccess}
            />
          )}
          {showStoreDisableModal && (
            <StoreActivationModal
              visible={showStoreDisableModal}
              isActivate={false}
              currentItem={currentItem}
              onCancel={onModalCancel}
              onSuccess={onModalSuccess}
            />
          )}
        </>
      )}
    </div>
  )
}

export default withDefaultLayout(StoreList)
